'use client';

import { InfoCircle, Lock } from 'iconsax-react';
import { X } from 'react-feather';

import { useMobile } from '@shared/hooks';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@shared/ui';

import { language } from '../model/language';
import type { TGoldTradingRestrictionVariant } from '../model/types';

type GoldTradingRestrictionModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  variant?: TGoldTradingRestrictionVariant;
};

export const GoldTradingRestrictionModal = ({
  open,
  onClose,
  title,
  description,
  variant = 'warning',
}: GoldTradingRestrictionModalProps) => {
  const isMobile = useMobile();
  const Icon = variant === 'danger' ? Lock : InfoCircle;
  const iconClassName = variant === 'danger' ? 'text-red-1' : 'text-orange';

  const content = (
    <p className='text-sm leading-7 text-text px-5 pt-5 pb-2 text-right mb-[125px]'>{description}</p>
  );

  const footer = (
    <div
      className={
        isMobile ? 'mt-5 px-2 pb-2' : 'flex flex-col gap-4 p-5 bg-modalBg mt-4 rounded-md'
      }
    >
      <Button
        className={
          isMobile
            ? 'h-[60px] w-full gap-x-1 text-xs font-bold text-base'
            : 'h-[60px] w-full gap-x-1 items-center flex justify-center text-[14px]'
        }
        type='button'
        onClick={onClose}
      >
        {language.ok}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
        <DrawerContent className='min-h-0'>
          <DrawerHeader className='flex flex-row items-center justify-between border-b border-border h-[67px] px-5'>
            <div className='flex flex-row items-center gap-2'>
              <DrawerTitle className='text-right text-text body-13-bold'>{title}</DrawerTitle>
            </div>
            <DrawerClose asChild>
              <button type='button' aria-label='close' onClick={onClose}>
                <X size={24} strokeWidth={1} />
              </button>
            </DrawerClose>
          </DrawerHeader>
          {content}
          {footer}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className='max-w-[455px]'>
        <DialogHeader className='flex flex-row items-center justify-between border-b border-border px-5 h-[67px]'>
          <div className='flex flex-row items-center gap-2'>
            <DialogTitle className='text-right text-text body-13-bold'>{title}</DialogTitle>
          </div>
          <DialogClose asChild>
            <button
              type='button'
              aria-label='close'
              className='flex items-center justify-center h-7 w-7 rounded-lg hover:bg-hover'
              onClick={onClose}
            >
              <X size={24} strokeWidth={1} />
            </button>
          </DialogClose>
        </DialogHeader>
        {content}
        {footer}
      </DialogContent>
    </Dialog>
  );
};
